/**
 * Leads: what arrives from the website, and what we do with it next.
 *
 * The website (Ryoma-Website) has two Netlify forms: "enquiry" (contact form) and "fit-check"
 * (the free shipping check). Netlify posts each submission to the platform
 * (/api/website-lead, src/server/api.ts). This file turns a submission into a lead, keeping only
 * the fields we need, and works out the first steps: how good a fit it is, what to offer,
 * when the reply is due and a draft reply. The website promises a reply within 2 working days.
 */
import { PRICE_BANDS, type PriceBand } from "./pricing.ts";
import { replyDue } from "./working-days.ts";

/** Touches in the outbound sequence (lib/crm.ts, SEQUENCE). */
export const SEQUENCE_LENGTH = 4;

/** The website's reply promise (Ryoma-Website src/site.json, replyTime). */
export const REPLY_WORKING_DAYS = 2;
export const ORDER_BANDS = ["Under 300", "300 to 999", "1,000 to 2,999", "3,000 to 7,999", "8,000 to 19,999", "20,000 or more"] as const;
export type OrderBand = (typeof ORDER_BANDS)[number];

export type LeadSource = "enquiry" | "fit-check" | "manual" | "import";
/** Where the opportunity came from: they contacted us, we approached them, or a partner introduced them. */
export type LeadOrigin = "inbound" | "outbound" | "partner";
export type LeadStatus =
  | "prospect" | "contacted" | "new" | "replied" | "call_booked" | "call_done" | "proposal_sent"
  | "won" | "not_a_fit" | "lost" | "no_response";

/**
 * The pipeline, in order. Outbound prospects start at "prospect" and move to "contacted" with the
 * first touch; website enquiries start at "new". Both meet at "replied". The last four close the lead.
 */
export const LEAD_STAGES: { status: LeadStatus; label: string; open: boolean }[] = [
  { status: "prospect", label: "Prospect: not contacted", open: true },
  { status: "contacted", label: "Contacted: sequence running", open: true },
  { status: "new", label: "New: reply due", open: true },
  { status: "replied", label: "Replied", open: true },
  { status: "call_booked", label: "Call booked", open: true },
  { status: "call_done", label: "Call done", open: true },
  { status: "proposal_sent", label: "Engagement letter sent", open: true },
  { status: "won", label: "Client", open: false },
  { status: "not_a_fit", label: "Not a fit", open: false },
  { status: "lost", label: "Lost", open: false },
  { status: "no_response", label: "No response", open: false },
];
export const ORIGIN_LABEL: Record<LeadOrigin, string> = { inbound: "Inbound", outbound: "Outbound", partner: "Partner introduction" };
export const STAGE_LABEL = Object.fromEntries(LEAD_STAGES.map((s) => [s.status, s.label])) as Record<LeadStatus, string>;
export const isOpen = (s: LeadStatus) => LEAD_STAGES.find((x) => x.status === s)?.open ?? false;

/** The fields we keep from a submission. Nothing else is stored: no IP address, browser or referrer. */
export interface LeadInput {
  source: LeadSource;
  netlify_id: string | null;
  received_at: string;
  name: string;
  email: string | null;
  company: string | null;
  orders_band: OrderBand | null;
  request: "call" | "review" | "other" | null;
  how_you_ship: string | null;
  carriers: string[];
  concerns: string[];
  timing: string | null;
  verdict: string | null;
  message: string | null;
  source_page: string | null;
  reply_due: string | null;
}

export interface LeadRow extends LeadInput {
  id: string;
  status: LeadStatus;
  owner: string | null;
  replied_at: string | null;
  call_at: string | null;
  next_step: string | null;
  next_step_due: string | null;
  last_contact_at: string | null;
  closed_reason: string | null;
  client_id: string | null;
  audit_id: string | null;
  purge_after: string | null;
  created_at: string;
  updated_at: string;
  // CRM (migration 20261006000005_crm.sql)
  origin: LeadOrigin;
  website: string | null;
  sector: string | null;
  partner_id: string | null;
  touch_count: number;
  last_touch_at: string | null;
  next_touch_due: string | null;
  do_not_contact: boolean;
  call_done_at: string | null;
  won_at: string | null;
  closed_at: string | null;
  // Sources and tags (migration 20261008000001_lead_sources.sql). Optional: older databases lack them.
  source_channel?: string | null;
  source_detail?: string | null;
  tags?: string[];
  // Legal form, for PECR (migration 20261011000001_lead_legal_form.sql). Optional: older databases lack it.
  legal_form?: LegalForm | null;
}

/** A lead's legal form. Sole traders and partnerships need consent before a marketing email (PECR). */
export type LegalForm = "limited" | "llp" | "sole_trader" | "partnership" | "other";

/** Extra fields for a lead we add ourselves (outbound prospects, partner introductions). */
export type LeadExtras = Partial<Pick<LeadRow, "origin" | "website" | "sector" | "partner_id" | "status" | "owner" | "next_touch_due" | "source_channel" | "source_detail" | "tags" | "legal_form">>;

export interface LeadNote {
  id?: string;
  lead_id: string;
  at?: string;
  by_email?: string | null;
  kind: "note" | "status" | "email" | "call" | "system" | "touch";
  body: string;
}

export type LeadPatch = Partial<Pick<LeadRow,
  "status" | "owner" | "replied_at" | "call_at" | "next_step" | "next_step_due" | "last_contact_at" | "closed_reason" |
  "client_id" | "audit_id" | "name" | "email" | "company" | "orders_band" | "message" |
  "origin" | "website" | "sector" | "partner_id" | "touch_count" | "last_touch_at" | "next_touch_due" | "do_not_contact" |
  "call_done_at" | "won_at" | "closed_at" | "source_channel" | "source_detail" | "tags" | "legal_form">>;

/** The fit check's concern codes, in the website's own words (Ryoma-Website fit-check.js, FOCUS). */
export const CONCERNS: Record<string, string> = {
  rates: "Carrier rates keep going up",
  surcharges: "Surcharges and volumetric weight",
  packaging: "Packaging costs too much",
  damage: "Damage and returns",
  renewal: "A carrier contract is up for renewal",
  unsure: "Not sure where the money goes",
};

const REQUEST_LABEL = { call: "A 30-minute call", review: "A Shipping Review", other: "Just a question" } as const;
export const requestLabel = (r: LeadInput["request"]) => (r ? REQUEST_LABEL[r] : null);

// ----------------------------------------------------------------------------- intake

const clip = (v: unknown, max: number): string | null => {
  if (typeof v !== "string") return null;
  const t = v.replace(/\s+/g, " ").trim();
  return t ? t.slice(0, max) : null;
};
const clipText = (v: unknown, max: number): string | null => {
  if (typeof v !== "string") return null;
  const t = v.replace(/\r\n?/g, "\n").trim();
  return t ? t.slice(0, max) : null;
};
/** A checkbox group can arrive as a list or as one comma-separated string. */
const list = (v: unknown, allowed?: string[]): string[] => {
  const raw = Array.isArray(v) ? v : typeof v === "string" ? v.split(",") : [];
  const out = raw.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean).map((x) => x.slice(0, 60));
  return [...new Set(allowed ? out.filter((x) => allowed.includes(x)) : out)].slice(0, 12);
};
const emailOf = (v: unknown): string | null => {
  const e = clip(v, 254)?.toLowerCase() ?? null;
  return e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : null;
};
const bandOf = (v: unknown): OrderBand | null => {
  const t = clip(v, 40);
  return (ORDER_BANDS as readonly string[]).includes(t ?? "") ? (t as OrderBand) : null;
};

/** The parts of a Netlify form submission we read. Netlify sends more; it is ignored. */
export interface NetlifySubmission {
  id?: string;
  form_name?: string;
  created_at?: string;
  data?: Record<string, unknown>;
}

/**
 * A website submission as a lead, or null when it is not one of our forms or has no name.
 * Spam caught by the honeypot never reaches here: Netlify drops it.
 */
export function leadFromSubmission(sub: NetlifySubmission, now = new Date()): LeadInput | null {
  const form = sub.form_name;
  if (form !== "enquiry" && form !== "fit-check") return null;
  const d = sub.data ?? {};
  const name = clip(d.name, 200);
  if (!name) return null;
  const received = sub.created_at && !Number.isNaN(Date.parse(sub.created_at)) ? new Date(sub.created_at) : now;
  const request = clip(d.enquiry, 10);
  return {
    source: form,
    netlify_id: clip(sub.id, 64),
    received_at: received.toISOString(),
    name,
    email: emailOf(d.email),
    company: clip(d.company, 200),
    orders_band: bandOf(d.orders),
    request: form === "enquiry" && (request === "call" || request === "review" || request === "other") ? request : null,
    how_you_ship: form === "fit-check" ? clip(d["how-you-ship"], 100) : null,
    carriers: form === "fit-check" ? list(d.carriers) : [],
    concerns: form === "fit-check" ? list(d.concerns, Object.keys(CONCERNS)) : [],
    timing: form === "fit-check" ? clip(d.timing, 100) : null,
    verdict: form === "fit-check" ? clip(d.verdict, 200) : null,
    message: clipText(d.message, 4000),
    source_page: clip(d["source-page"], 200),
    reply_due: replyDue(received, REPLY_WORKING_DAYS).toISOString(),
  };
}

// ----------------------------------------------------------------------------- signature

const enc = new TextEncoder();
const hex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
function b64urlDecode(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4);
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}
function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

/**
 * Checks Netlify's X-Webhook-Signature: a JWT signed (HS256) with the JWS secret set on the
 * form notification, whose claims are iss "netlify" and sha256, the hex SHA-256 of the body.
 */
export async function verifyNetlifySignature(token: string | null, body: string, secret: string): Promise<boolean> {
  if (!token || !secret) return false;
  const parts = token.split(".");
  if (parts.length !== 3) return false;
  const [h, p, sig] = parts as [string, string, string];
  let header: { alg?: string };
  let claims: { iss?: string; sha256?: string };
  try {
    header = JSON.parse(new TextDecoder().decode(b64urlDecode(h)));
    claims = JSON.parse(new TextDecoder().decode(b64urlDecode(p)));
  } catch {
    return false;
  }
  if (header.alg !== "HS256" || claims.iss !== "netlify" || typeof claims.sha256 !== "string") return false;
  const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);
  const expected = new Uint8Array(await crypto.subtle.sign("HMAC", key, enc.encode(`${h}.${p}`)));
  if (!sameBytes(expected, b64urlDecode(sig))) return false;
  const bodyHash = hex(await crypto.subtle.digest("SHA-256", enc.encode(body)));
  return sameBytes(enc.encode(claims.sha256.toLowerCase()), enc.encode(bodyHash));
}

// ----------------------------------------------------------------------------- triage

export type Fit = "guide" | "review" | "audit" | "quote" | "unknown";

export interface Triage {
  fit: Fit;
  /** What to offer, with the published price */
  offer: string;
  band: PriceBand | null;
  priority: "high" | "normal" | "low";
  /** Points to check before replying */
  checks: string[];
}

const FIT_BY_BAND: Record<OrderBand, Fit> = {
  "Under 300": "guide", "300 to 999": "review",
  "1,000 to 2,999": "audit", "3,000 to 7,999": "audit", "8,000 to 19,999": "audit", "20,000 or more": "quote",
};
const MIDPOINT: Record<OrderBand, number> = {
  "Under 300": 150, "300 to 999": 650, "1,000 to 2,999": 2000, "3,000 to 7,999": 5000, "8,000 to 19,999": 12000, "20,000 or more": 25000,
};

const pounds = (p: number) => `£${(p / 100).toLocaleString("en-GB")}`;

/** Fit, offer and priority: the same bands as the website's fit check and pricing page. */
export function triage(lead: Pick<LeadInput, "orders_band" | "request" | "how_you_ship" | "timing" | "concerns" | "email" | "company">): Triage {
  const fit: Fit = lead.orders_band ? FIT_BY_BAND[lead.orders_band] : "unknown";
  const band = lead.orders_band ? PRICE_BANDS.find((b) => MIDPOINT[lead.orders_band!] >= b.min && MIDPOINT[lead.orders_band!] <= b.max) ?? null : null;
  const offer =
    fit === "guide" ? "Free shipping guide"
    : fit === "review" ? `Shipping Review, ${pounds(29500)}`
    : fit === "audit" && band ? `Audit, ${pounds(band.feePence)} (${band.label})`
    : fit === "quote" ? "Audit with a tailored quote (20,000 or more orders a month)"
    : "Not known yet: ask their monthly orders";
  const keen = lead.timing === "Now" || lead.request === "call" || lead.concerns.includes("renewal");
  const auditSize = fit === "audit" || fit === "quote";
  const priority = auditSize && keen ? "high" : fit === "guide" || (!auditSize && lead.timing === "Just exploring") ? "low" : "normal";
  const checks: string[] = [];
  if (!lead.email) checks.push("No valid email: find a way to reply before the deadline.");
  if (lead.company) checks.push(`Look at ${lead.company}: what they sell, likely parcel sizes, and the carriers on their delivery page.`);
  if (lead.how_you_ship === "Fulfilment partner (3PL)") checks.push("A 3PL ships for them: ask what they control (rates, boxes, carrier) before offering an audit.");
  if (lead.how_you_ship === "Shop or shipping platform rates") checks.push("They use platform rates: ask whether they would open their own carrier accounts.");
  if (lead.concerns.includes("renewal")) checks.push("A carrier contract is up for renewal: ask the date, as it sets the timetable.");
  if (fit === "unknown") checks.push("Ask their orders a month to judge fit.");
  return { fit, offer, band, priority, checks };
}

// ----------------------------------------------------------------------------- reply

const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

/**
 * A first reply to edit and send from business@ryoma-insight.co.uk. Plain British English, no
 * promises about savings (the website never estimates them). Placeholders are in [square brackets].
 */
export function draftReply(lead: Pick<LeadInput, "name" | "source" | "orders_band" | "request" | "concerns" | "how_you_ship" | "timing" | "email" | "company" | "message">, sender = "[Your name]"): { subject: string; body: string } {
  const t = triage(lead);
  const hi = `Hello ${firstName(lead.name)},`;
  const thanks = lead.source === "fit-check" ? "Thank you for completing our free shipping check." : "Thank you for getting in touch.";
  const focus = lead.concerns.map((c) => CONCERNS[c]).filter(Boolean).map((c) => c!.toLowerCase());
  const focusLine = focus.length ? ` You mentioned ${focus.length === 1 ? focus[0] : `${focus.slice(0, -1).join(", ")} and ${focus.at(-1)}`}, so we would start there.` : "";
  const sign = `Best wishes,\n${sender}\nRyoma Insight\nbusiness@ryoma-insight.co.uk`;
  let subject: string;
  let middle: string;
  switch (t.fit) {
    case "guide":
      subject = "Your shipping check: our free guide";
      middle =
        "At under 300 orders a month, paid work rarely pays for itself yet. Our free shipping guide covers the fixes that make the biggest difference: https://ryoma-insight.co.uk/shipping-guide.html\n\n" +
        "If your volume grows, or you have a specific question, just reply to this email.";
      break;
    case "review":
      subject = "Your shipping check: the Shipping Review";
      middle =
        `At your size, the Shipping Review is the right fit: a 45-minute call and a one-page action plan built from your own data, which you carry out yourself. It costs £295 ex VAT, and if you grow past 1,000 orders a month the fee comes off an audit.${focusLine}\n\n` +
        "Would any of these times suit for a short call to set it up? [two or three times]";
      break;
    case "audit":
    case "quote":
      subject = lead.source === "fit-check" ? "Your shipping check: next step" : "Your enquiry: a 30-minute call";
      middle =
        `From what you have told us, you look like a good fit for an audit.${focusLine}\n\n` +
        "The next step is a 30-minute call, so we can understand how you ship today and confirm whether we can help. Would any of these times suit? [two or three times]\n\n" +
        "There is nothing to prepare, and please do not send any files yet. If we agree to work together, we will send an engagement letter and a secure way to share your order data.";
      break;
    default:
      subject = "Your enquiry";
      middle = "[Answer their question.]\n\nSo we can point you to the right next step, roughly how many orders do you send a month?";
  }
  if (lead.message) middle = `[Reply to their message: "${lead.message.slice(0, 200)}${lead.message.length > 200 ? "…" : ""}"]\n\n${middle}`;
  return { subject, body: `${hi}\n\n${thanks} ${middle}\n\n${sign}` };
}

/** A mailto: link that opens the draft in the mail app. */
export function mailtoLink(to: string, draft: { subject: string; body: string }): string {
  return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
}

// ----------------------------------------------------------------------------- what needs doing

export interface LeadDue {
  lead: LeadRow;
  what: string;
  due: string | null;
  overdue: boolean;
}

/** The next thing to do on an open lead, and whether it is late. */
export function leadNextAction(lead: LeadRow, now = new Date()): LeadDue | null {
  if (!isOpen(lead.status) || lead.do_not_contact) return null;
  const late = (iso: string | null) => !!iso && Date.parse(iso.length === 10 ? `${iso}T23:59:59Z` : iso) < now.getTime();
  if (lead.status === "new") return { lead, what: "Reply", due: lead.reply_due, overdue: late(lead.reply_due) };
  if (lead.status === "prospect") return { lead, what: lead.next_step ?? "Touch 1", due: lead.next_step_due ?? lead.next_touch_due, overdue: late(lead.next_step_due ?? lead.next_touch_due) };
  if (lead.status === "contacted") {
    const n = lead.touch_count ?? 0;
    const what = n >= SEQUENCE_LENGTH ? "No reply after the last touch: close as no response" : `Touch ${n + 1}`;
    return { lead, what, due: lead.next_touch_due, overdue: late(lead.next_touch_due) };
  }
  if (lead.next_step) return { lead, what: lead.next_step, due: lead.next_step_due, overdue: late(lead.next_step_due) };
  if (lead.status === "call_booked") return { lead, what: "Discovery call", due: lead.call_at, overdue: false };
  if (lead.status === "call_done") return { lead, what: "Send the engagement letter, or close", due: null, overdue: false };
  if (lead.status === "proposal_sent") return { lead, what: "Follow up the engagement letter", due: null, overdue: false };
  return { lead, what: "Agree the next step", due: null, overdue: false };
}
